import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiUrl, apiUnreachableMessage } from '../config/api';
import { CompanySelectField } from './CompanySelectField';
import { DateRangePicker } from './DateRangePicker';
import { useAdminCompanies } from '../utils/useAdminCompanies';
import { getStoredUser } from '../utils/auth';
import { formatMonthLabel } from '../utils/formSubmitMeta';

const inputClass =
  'w-full h-9 box-border px-3 text-sm bg-black border border-gray-700 rounded-md text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-600';
const labelClass = 'block text-sm font-medium text-gray-300 mb-1 leading-5';

function adminHeadersJson() {
  const user = getStoredUser();
  return {
    'Content-Type': 'application/json',
    'X-User-Email': user?.email || '',
  };
}

function FilterSelect({ id, label, value, onChange, disabled, options, placeholder }) {
  return (
    <div className="min-w-0">
      <label htmlFor={id} className={labelClass}>
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={onChange}
        disabled={disabled}
        className={inputClass}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function formatSubmittedDate(row) {
  if (row?.submitted_at) {
    const date = new Date(row.submitted_at);
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    }
  }
  if (row?.submitted_month || row?.submitted_year) {
    return `${formatMonthLabel(row.submitted_month)} ${row.submitted_year || ''}`.trim();
  }
  return '—';
}

function roleLabel(role) {
  if (role === 'admin' || role === 'super_admin' || role === 'super_user') return 'Supervisor';
  if (role === 'user') return 'User/Worker';
  return role || '—';
}

export function SubmittedFormsPanel({ onBack, lockedCompanyId, lockedCompanyName, ownOnly = false }) {
  const { companies, loading: companiesLoading, error: companiesError, reload } =
    useAdminCompanies({ enabled: !lockedCompanyId && !ownOnly });

  const [companyId, setCompanyId] = useState(lockedCompanyId ? String(lockedCompanyId) : '');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [siteProject, setSiteProject] = useState('');
  const [roleGroup, setRoleGroup] = useState('');
  const [userId, setUserId] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const [filterOptions, setFilterOptions] = useState({
    sites: [],
    users: [],
    role_groups: [],
  });
  const [submissions, setSubmissions] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [loadingResults, setLoadingResults] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (lockedCompanyId) {
      setCompanyId(String(lockedCompanyId));
    }
  }, [lockedCompanyId]);

  const buildFilterParams = useCallback(
    (overrides = {}) => {
      const params = new URLSearchParams();
      const values = ownOnly
        ? {
            date_from: dateFrom,
            date_to: dateTo,
            site_project: siteProject,
            search,
            ...overrides,
          }
        : {
            company_id: companyId,
            date_from: dateFrom,
            date_to: dateTo,
            site_project: siteProject,
            role_group: roleGroup,
            user_id: userId,
            search,
            ...overrides,
          };
      Object.entries(values).forEach(([key, val]) => {
        if (val !== '' && val != null) params.set(key, String(val));
      });
      return params;
    },
    [companyId, dateFrom, dateTo, siteProject, roleGroup, userId, search, ownOnly]
  );

  const filtersUrl = ownOnly
    ? '/api/users/form-submissions/filters'
    : '/api/admin/form-submissions/filters';
  const listUrl = ownOnly ? '/api/users/form-submissions' : '/api/admin/form-submissions';

  const loadFilterOptions = useCallback(async () => {
    setLoadingFilters(true);
    try {
      const params = buildFilterParams();
      ['user_id', 'search'].forEach((key) => params.delete(key));
      const res = await fetch(apiUrl(`${filtersUrl}?${params}`), {
        headers: adminHeadersJson(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || 'Failed to load filter options');
        return;
      }
      setFilterOptions(data);
    } catch {
      setError(apiUnreachableMessage());
    } finally {
      setLoadingFilters(false);
    }
  }, [buildFilterParams, filtersUrl]);

  const loadSubmissions = useCallback(async () => {
    setLoadingResults(true);
    setError('');
    try {
      const params = buildFilterParams();
      const res = await fetch(apiUrl(`${listUrl}?${params}`), {
        headers: adminHeadersJson(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || 'Failed to load submitted forms');
        return;
      }
      setSubmissions(data.submissions || []);
    } catch {
      setError(apiUnreachableMessage());
    } finally {
      setLoadingResults(false);
    }
  }, [buildFilterParams, listUrl]);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const siteOptions = useMemo(
    () => (filterOptions.sites || []).map((site) => ({ value: site, label: site })),
    [filterOptions.sites]
  );
  const userOptions = useMemo(
    () =>
      (filterOptions.users || []).map((user) => ({
        value: String(user.id),
        label: user.name || user.email,
      })),
    [filterOptions.users]
  );
  const roleOptions = useMemo(
    () =>
      (filterOptions.role_groups || []).map((group) => ({
        value: group.value,
        label: group.label,
      })),
    [filterOptions.role_groups]
  );

  const handleCompanyChange = (e) => {
    setCompanyId(e.target.value);
    setDateFrom('');
    setDateTo('');
    setSiteProject('');
    setRoleGroup('');
    setUserId('');
  };

  const handleDateRangeChange = ({ dateFrom: nextFrom, dateTo: nextTo }) => {
    setDateFrom(nextFrom || '');
    setDateTo(nextTo || '');
    setSiteProject('');
    setUserId('');
  };

  const handleSiteChange = (e) => {
    setSiteProject(e.target.value);
    setUserId('');
  };

  const handleRoleChange = (e) => {
    setRoleGroup(e.target.value);
    setUserId('');
  };

  const companyReady = ownOnly || Boolean(companyId || lockedCompanyId);

  return (
    <div className="max-w-none w-full">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="text-sm text-gray-400 hover:text-white flex items-center gap-1 shrink-0"
          >
            ← Back to dashboard
          </button>
        ) : (
          <span />
        )}
        <div className="w-full sm:w-72 md:w-80 lg:w-96 shrink-0 sm:ml-auto">
          <label htmlFor="submitted-forms-search" className="sr-only">
            Search
          </label>
          <input
            id="submitted-forms-search"
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={ownOnly ? 'Search site or form...' : 'Search company, user, site, form...'}
            className={inputClass}
          />
        </div>
      </div>

      <h2 className="text-xl font-semibold text-white mb-0.5">All Submitted Forms</h2>
      <p className="text-gray-400 text-xs mb-3">
        {ownOnly
          ? 'Your submitted form PDFs only.'
          : 'Filter and search submitted form PDFs.'}
      </p>

      {error && (
        <div className="text-red-400 text-sm bg-red-400/10 py-1.5 px-3 rounded mb-3">{error}</div>
      )}

      <div className={`grid gap-2 sm:grid-cols-2 ${ownOnly ? 'lg:grid-cols-2' : 'lg:grid-cols-3 xl:grid-cols-5'} mb-4`}>
        {ownOnly ? null : lockedCompanyId ? (
          <div className="min-w-0">
            <label className={labelClass}>Company</label>
            <input
              value={lockedCompanyName || 'Your company'}
              readOnly
              className={`${inputClass} text-gray-400 cursor-not-allowed`}
            />
          </div>
        ) : (
          <CompanySelectField
            companies={companies}
            loading={companiesLoading}
            error={companiesError}
            value={companyId}
            onChange={handleCompanyChange}
            onRefresh={reload}
            showHints={false}
            required={false}
            compact
            label="Choose Company"
            id="submitted-company"
          />
        )}

        <DateRangePicker
          id="submitted-date-range"
          label="Date range"
          dateFrom={dateFrom}
          dateTo={dateTo}
          onChange={handleDateRangeChange}
          disabled={!companyReady}
          placeholder="Select from – to dates"
          hint={!ownOnly && !companyReady ? 'Choose company first' : ''}
        />

        <FilterSelect
          id="submitted-site"
          label="Select Sites/Projects"
          value={siteProject}
          onChange={handleSiteChange}
          disabled={loadingFilters || !companyReady}
          options={siteOptions}
          placeholder="All sites/projects"
        />
        {ownOnly ? null : (
          <>
        <FilterSelect
          id="submitted-role"
          label="Supervisor or User"
          value={roleGroup}
          onChange={handleRoleChange}
          disabled={loadingFilters}
          options={roleOptions}
          placeholder="All roles"
        />
        <FilterSelect
          id="submitted-user"
          label="Names of Users"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          disabled={loadingFilters}
          options={userOptions}
          placeholder="All users"
        />
          </>
        )}
      </div>

      <div className="flex items-center justify-between gap-3 mb-3">
        <p className="text-xs text-gray-400">
          {loadingResults
            ? 'Loading submissions...'
            : `${submissions.length} submitted form(s) found`}
          {dateFrom || dateTo ? ` · ${dateFrom || '…'} → ${dateTo || '…'}` : ''}
        </p>
        <button
          type="button"
          onClick={() => {
            loadFilterOptions();
            loadSubmissions();
          }}
          className="text-xs text-amber-200/80 hover:text-amber-200 underline"
        >
          Refresh
        </button>
      </div>

      {!loadingResults && submissions.length === 0 ? (
        <p className="text-gray-500 text-sm py-4 text-center border border-gray-800 rounded-md">
          No submitted forms match your filters yet.
        </p>
      ) : (
        <div className="overflow-x-auto border border-gray-800 rounded-md">
          <table className="w-full text-sm text-left">
            <thead className="bg-gray-900 text-gray-400 uppercase text-xs">
              <tr>
                <th className="px-3 py-2">Form</th>
                <th className="px-3 py-2">Company</th>
                <th className="px-3 py-2">User</th>
                <th className="px-3 py-2">Site / Project</th>
                <th className="px-3 py-2">Supervisor</th>
                <th className="px-3 py-2">Submitted</th>
                <th className="px-3 py-2 text-right">PDF</th>
              </tr>
            </thead>
            <tbody>
              {submissions.map((row) => (
                <tr key={row.id} className="border-t border-gray-800 hover:bg-gray-900/50">
                  <td className="px-3 py-2 text-gray-200">{row.form_name}</td>
                  <td className="px-3 py-2 text-gray-300">{row.company_name || '—'}</td>
                  <td className="px-3 py-2 text-gray-300">
                    <div>{row.user_name || '—'}</div>
                    <div className="text-xs text-gray-500">{roleLabel(row.user_role)}</div>
                  </td>
                  <td className="px-3 py-2 text-gray-300">{row.site_project || '—'}</td>
                  <td className="px-3 py-2 text-gray-300">{row.supervisor_name || '—'}</td>
                  <td className="px-3 py-2 text-gray-300 whitespace-nowrap">
                    {formatSubmittedDate(row)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {row.file_url ? (
                      <a
                        href={apiUrl(row.file_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-amber-200/90 hover:text-amber-200 underline text-xs"
                      >
                        View PDF
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
